import React, { useCallback, useMemo, useState } from "react";

import { isAxiosError } from "axios";
import { orderBy } from "lodash";
import { useParams } from "react-router-dom";

import useAllAccounts from "@fartherfinance/frontend/api/Accounts/hooks/useAllAccounts";
import useClientProfile from "@fartherfinance/frontend/api/Entity/hooks/useClientProfile";
import useDeleteAllGoalBasedFinancialPlanAccounts from "@fartherfinance/frontend/api/FinancialPlans/account/hooks/useDeleteAllGoalBasedFinancialPlanAccounts";
import useDeleteGoalBasedFinancialPlanAccount from "@fartherfinance/frontend/api/FinancialPlans/account/hooks/useDeleteGoalBasedFinancialPlanAccount";
import usePatchGoalBasedFinancialPlanAccount from "@fartherfinance/frontend/api/FinancialPlans/account/hooks/usePatchGoalBasedFinancialPlanAccount";
import usePostAllGoalBasedFinancialPlanAccounts from "@fartherfinance/frontend/api/FinancialPlans/account/hooks/usePostAllGoalBasedFinancialPlanAccounts";
import usePostGoalBasedFinancialPlanAccount from "@fartherfinance/frontend/api/FinancialPlans/account/hooks/usePostGoalBasedFinancialPlanAccount";
import { GoalBasedFinancialPlanAccountResponse } from "@fartherfinance/frontend/api/FinancialPlans/account/types";
import {
  GoalBasedFinancingMethodSchema,
  GoalBasedGoalType,
} from "@fartherfinance/frontend/api/FinancialPlans/goals/types";
import {
  GoalBasedFinancialPlanAccountId,
  GoalBasedFinancialPlanId,
} from "@fartherfinance/frontend/api/FinancialPlans/ids";
import useGetGoalBasedFinancialPlan from "@fartherfinance/frontend/api/FinancialPlans/plan/hooks/useGetGoalBasedFinancialPlan";
import useAllAccountBalancesV4 from "@fartherfinance/frontend/api/PerformanceGroups/hooks/useAllAccountBalancesV4";
import useInvitations from "@fartherfinance/frontend/api/Sharing/hooks/useInvitations";
import {
  AltAssetId,
  ClientId,
  ExternalAccountId,
  FartherAccountId,
} from "@fartherfinance/frontend/api/Types";

import FilledGoalCard from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Goals/FilledGoalCard/FilledGoalCard";
import AsideTooltip from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/AsideTooltip/AsideTooltip";
import Card from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/Card/Card";
import CardLoading from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/Card/CardLoading";
import CardTitleLoading from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/Card/CardTitleLoading";
import CardsGrid from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/CardsGrid/CardsGrid";
import { goalOptions } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/constants";
import ContentWithAside from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/ContentWithAside/ContentWithAside";
import FilledCard from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/FilledCard/FilledCard";
import {
  isAssetLinkedInAnotherCategoryCard,
  Section,
} from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/helpers/isAssetLinkedInAnotherCategoryCard";
import { ClientAndSpouseDetails } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/hooks/useGetClientAndSpouseDetails";
import { ErrorMessage } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import BodyContentFormatter from "@src/sharedComponents/BodyContentFormatter/BodyContentFormatter";
import Checkbox from "@src/sharedComponents/Checkbox/Checkbox";
import LinkButton from "@src/sharedComponents/LinkButton/LinkButton";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import Text from "@src/sharedComponents/Typography/Text";
import { formatExternalAccountName } from "@src/utils/ExternalAccount/formatExternalAccountName";
import { getExternalAccountBalance } from "@src/utils/ExternalAccount/getExternalAccountBalance";
import { getExternalAccountName } from "@src/utils/ExternalAccount/getExternalAccountName";
import { pluralize } from "@src/utils/pluralize";
import toUSD from "@src/utils/toUSD";

import AssetsAndLiabilitiesFormDrawer, { ExtraAccountData } from "./Form";
import {
  AssetLiabilityPlatformTypeAccountId,
  ManualOrExternalAccountTypeTuple,
  PlatformTypeExternalAccountIdObj,
  PlatformTypeFartherManagedAccountIdObj,
  PlatformTypeManuallyTrackedAccountIdObj,
} from "./Types";

import styles from "./Assets.module.css";

const genTooltipText = (isLinked: boolean, section: Section) => {
  if (isLinked == false || section == undefined) {
    return undefined;
  }

  return `This account cannot be removed because it is linked to an item in the ${section} step. Remove that item in order to remove this account from your plan.`;
};

const capitalizeFirstLetter = (str: string) => {
  return `${str.substring(0, 1).toUpperCase()}${str.substring(1)}`;
};

const NoExtraAccountData: ExtraAccountData = {
  marketValue: undefined,
  returns: undefined,
  volatility: undefined,
};

interface Props {
  clientSpouseData: ClientAndSpouseDetails;
}

const Assets: React.FC<Props> = ({ clientSpouseData }) => {
  const { clientId, goalBasedFinancialPlanId } = useParams<{
    clientId: ClientId;
    goalBasedFinancialPlanId: GoalBasedFinancialPlanId;
  }>();

  const [showDrawer, setShowDrawer] = useState<boolean>(false);

  const [curAccount, setCurAccount] = useState<
    AssetLiabilityPlatformTypeAccountId | undefined
  >(undefined);

  const [existingAccountData, setExistingAccountData] = useState<
    GoalBasedFinancialPlanAccountResponse | undefined
  >(undefined);
  const [formTitle, setFormTitle] = useState<string>("");
  const [ownerName, setOwnerName] = useState<string>("");
  const [accountDisplayName, setAccountDisplayName] = useState<string>("");

  const [mutatingCard, setMutatingCard] = useState<
    FartherAccountId | AltAssetId | ExternalAccountId | null
  >(null);

  const notify = useStatusNotification();

  const auth = useRequestAuth();
  const allAccounts = useAllAccounts(clientId, auth);
  const balances = useAllAccountBalancesV4(clientId, false, auth);
  const financialPlan = useGetGoalBasedFinancialPlan(
    goalBasedFinancialPlanId,
    auth
  );
  const profile = useClientProfile(clientId, auth);
  const invitations = useInvitations(auth);

  const {
    mutateAsync: postGoalBasedFinancialPlanAccount,
    isLoading: isMutatingPost,
  } = usePostGoalBasedFinancialPlanAccount(auth);
  const {
    mutateAsync: postAllGoalBasedFinancialPlanAccounts,
    isLoading: isMutatingPostAll,
  } = usePostAllGoalBasedFinancialPlanAccounts(auth);
  const {
    mutateAsync: patchGoalBasedFinancialPlanAccount,
    isLoading: isMutatingPatch,
  } = usePatchGoalBasedFinancialPlanAccount(auth);
  const {
    mutateAsync: deleteGoalBasedFinancialPlanAccount,
    isLoading: isMutatingDelete,
  } = useDeleteGoalBasedFinancialPlanAccount(auth);
  const {
    mutateAsync: deleteAllGoalBasedFinancialPlanAccounts,
    isLoading: isMutatingDeleteAll,
  } = useDeleteAllGoalBasedFinancialPlanAccounts(auth);

  // Wrapper function for adding assets with event tracking
  const postAssetWithEventTracking = useCallback(
    async (params: Parameters<typeof postGoalBasedFinancialPlanAccount>[0]) => {
      const result = await postGoalBasedFinancialPlanAccount(params);

      // Track successful asset addition
      trackEvent({
        name: "Client Add Asset",
        properties: {
          clientId,
          planId: goalBasedFinancialPlanId,
          assetType: params.body.accountType,
          totalAssets: (financialPlan.data?.accounts?.length || 0) + 1,
        },
      });

      return result;
    },
    [
      postGoalBasedFinancialPlanAccount,
      clientId,
      goalBasedFinancialPlanId,
      financialPlan.data?.accounts?.length,
    ]
  );

  const { client, spouse } = clientSpouseData;

  const { fullName: clientFullName } = client;

  const spouseFullName = spouse?.fullName ?? "Spouse";

  const getNameOfClientOrSpouse = useCallback(
    (ownerId: ClientId) => {
      return ownerId === clientId ? clientFullName : spouseFullName;
    },
    [clientId, clientFullName, spouseFullName]
  );

  const onCloseDrawer = () => {
    setShowDrawer(false);
  };

  const allAssets = useMemo(() => {
    if (allAccounts.isLoading || allAccounts.hasError) {
      return {
        fartherAccounts: [],
        manuallyTrackedAccounts: [],
        externalAccounts: [],
      };
    }

    return {
      fartherAccounts: allAccounts.data.fartherAccounts,
      manuallyTrackedAccounts: allAccounts.data.manuallyTrackedAccounts,
      externalAccounts: allAccounts.data.externalAccounts.filter(
        (exAcct) =>
          exAcct.accountProvider === "plaid" &&
          exAcct.details.account.accountType !== "credit" &&
          exAcct.details.account.accountType !== "loan"
      ),
    };
  }, [allAccounts]);

  const allLiabilities = useMemo(() => {
    if (allAccounts.isLoading || allAccounts.hasError) {
      return [];
    }

    return allAccounts.data.externalAccounts.filter(
      (exAcct) =>
        exAcct.accountProvider === "plaid" &&
        (exAcct.details.account.accountType === "credit" ||
          exAcct.details.account.accountType === "loan")
    );
  }, [allAccounts]);

  const fartherAccountBalances = useMemo(() => {
    if (balances.isLoading || balances.hasError) {
      return [];
    }

    return balances.data;
  }, [balances]);

  const accountsInPlan = useMemo(() => {
    if (financialPlan.isLoading || financialPlan.hasError) {
      return [];
    }

    return financialPlan.data.accounts;
  }, [financialPlan]);

  const allClientsAccounts = useMemo(() => {
    if (allAccounts.isLoading || allAccounts.hasError) {
      return [];
    }

    const managed: PlatformTypeFartherManagedAccountIdObj[] =
      allAccounts.data.fartherAccounts.map((acct) => ({
        accountType: "FartherManagedAccount",
        accountId: acct.virtualAccountId,
      }));

    const manual: PlatformTypeManuallyTrackedAccountIdObj[] =
      allAccounts.data.manuallyTrackedAccounts.map((mta) => ({
        accountType: "ManuallyTrackedAccount",
        accountId: mta.id,
      }));

    const external: PlatformTypeExternalAccountIdObj[] =
      allAccounts.data.externalAccounts.map((ex) => ({
        accountType: "ExternalAccount",
        accountId: ex.accountId,
      }));

    return [...managed, ...manual, ...external];
  }, [allAccounts]);

  const areAllAccountsIncludedInPlan = useMemo(
    () =>
      allClientsAccounts.every((a) =>
        accountsInPlan.some((aip) => a.accountId === aip.accountId)
      ),
    [accountsInPlan, allClientsAccounts]
  );

  const sharedInvitations = useMemo(() => {
    if (invitations.isLoading || invitations.hasError) {
      return [];
    }

    return invitations.data.received.filter((i) => i.status === "Active");
  }, [invitations]);

  const onAddOrUpdateAccountData = (accountData: ExtraAccountData) => {
    if (existingAccountData === undefined) {
      if (curAccount === undefined) {
        return;
      }

      onAddAccountToPlan(curAccount, accountData);
    }

    onUpdateAccountDataInPlan(accountData);
  };

  const onAddAccountToPlan = async (
    [accountType, accountId]: AssetLiabilityPlatformTypeAccountId,
    extraAccountData: ExtraAccountData
  ) => {
    setMutatingCard(accountId);

    try {
      await postAssetWithEventTracking({
        goalBasedFinancialPlanId,
        body:
          accountType === "FartherManagedAccount"
            ? {
                accountType,
                accountId,
              }
            : accountType === "ManuallyTrackedAccount"
            ? {
                accountType,
                accountId,
                accountData: extraAccountData,
              }
            : {
                accountType,
                accountId,
                accountData: extraAccountData,
              },
      });

      onCloseDrawer();

      notify("Account added", "Success");
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        notify(`Error adding account: ${message ?? ""}`, "Error");
      } else {
        notify("Error adding account", "Error");
      }
    } finally {
      setMutatingCard(null);
    }
  };

  const onAddAllAccountsToPlan = async () => {
    try {
      await postAllGoalBasedFinancialPlanAccounts({
        goalBasedFinancialPlanId,
        body: {
          accounts: allClientsAccounts
            .filter((a) =>
              accountsInPlan.every((aip) => a.accountId !== aip.accountId)
            )
            .map((a) => {
              if (a.accountType === "FartherManagedAccount") {
                return a;
              }

              const accountInPlan = accountsInPlan.find(
                (aip) => aip.accountId === a.accountId
              );

              return {
                ...a,
                accountData: accountInPlan?.accountData?.data ?? {},
              };
            }),
        },
      });

      notify("All accounts added", "Success");
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        notify(`Error adding all accounts: ${message ?? ""}`, "Error");
      } else {
        notify("Error adding all accounts", "Error");
      }
    }
  };

  const onUpdateAccountDataInPlan = async (
    extraAccountData: ExtraAccountData
  ) => {
    if (curAccount === undefined) {
      return;
    }

    if (existingAccountData === undefined) {
      return;
    }

    const [accountType, accountId] = curAccount;

    if (accountType === "FartherManagedAccount") {
      return;
    }

    setMutatingCard(accountId);

    try {
      await patchGoalBasedFinancialPlanAccount({
        goalBasedFinancialPlanId,
        goalBasedFinancialPlanAccountId: existingAccountData.goalBasedAccountId,
        body:
          accountType === "ManuallyTrackedAccount"
            ? {
                accountType,
                accountId,
                accountData: extraAccountData,
              }
            : {
                accountType,
                accountId,
                accountData: extraAccountData,
              },
      });

      onCloseDrawer();

      notify("Account data updated", "Success");
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        notify(`Error updating account data: ${message ?? ""}`, "Error");
      } else {
        notify("Error updating account data", "Error");
      }
    } finally {
      setMutatingCard(null);
    }
  };

  const onRemoveAccountFromPlan = async (
    accountId: FartherAccountId | AltAssetId | ExternalAccountId,
    planAccountId: GoalBasedFinancialPlanAccountId
  ) => {
    setMutatingCard(accountId);

    try {
      await deleteGoalBasedFinancialPlanAccount({
        goalBasedFinancialPlanId,
        goalBasedFinancialPlanAccountId: planAccountId,
      });

      onCloseDrawer();

      notify("Account removed", "Success");
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        notify(`Error removing account: ${message ?? ""}`, "Error");
      } else {
        notify("Error removing account", "Error");
      }
    } finally {
      setMutatingCard(null);
    }
  };

  const onRemoveAllAccountsFromPlan = async () => {
    try {
      const accountsToRemove = accountsInPlan
        .filter((aip) => {
          const [isLinked, _] = isAssetLinkedInAnotherCategoryCard(
            aip.accountId,
            financialPlan.data
          );

          return !isLinked;
        })
        .map((a) => a.goalBasedAccountId);

      const accountsToRemoveLength = accountsToRemove.length;
      const accountsInPlanLength = accountsInPlan.length;

      await deleteAllGoalBasedFinancialPlanAccounts({
        goalBasedFinancialPlanId,
        goalBasedFinancialPlanAccountIds: accountsToRemove,
      });

      if (accountsToRemoveLength >= 1) {
        notify("Accounts removed", "Success");
      }

      if (accountsToRemoveLength < accountsInPlanLength) {
        notify(
          "Some accounts were not removed because they are linked.",
          "Warning"
        );
      }
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        notify(`Error removing accounts: ${message ?? ""}`, "Error");
      } else {
        notify("Error removing accounts", "Error");
      }
    }
  };

  const handleToggleAllAccounts = (isNowChecked: boolean) => {
    if (isNowChecked) {
      onAddAllAccountsToPlan();
    } else {
      onRemoveAllAccountsFromPlan();
    }
  };

  const onCheckboxChange = (
    isNowChecked: boolean,
    account: AssetLiabilityPlatformTypeAccountId
  ) => {
    const [_, accountId] = account;
    const accountInPlan = accountsInPlan.find((a) => a.accountId === accountId);
    if (isNowChecked) {
      onAddAccountToPlan(account, NoExtraAccountData);
    } else if (accountInPlan) {
      onRemoveAccountFromPlan(accountId, accountInPlan.goalBasedAccountId);
    } else {
      notify("Account removed", "Success");
    }
  };

  const getResourceOwnerName = useCallback(
    (ownerIds: ClientId[]) => {
      const thisClientsName = profile.data
        ? {
            fullName: `${profile.data.investorProfile.personalDetails.name.first} ${profile.data.investorProfile.personalDetails.name.last}`,
            firstName: profile.data.investorProfile.personalDetails.name.first,
          }
        : {
            fullName: "",
            firstName: "",
          };

      if (ownerIds.some((id) => id === clientId)) {
        return thisClientsName;
      }

      const ownerId = ownerIds[0];

      const invitationOfOwner = sharedInvitations.find(
        (inv) => inv.invitor.invitorId === ownerId
      );
      if (invitationOfOwner === undefined) {
        return {
          fullName: "",
          firstName: "",
        };
      }

      return {
        fullName: `${invitationOfOwner.invitor.firstName} ${invitationOfOwner.invitor.lastName}`,
        firstName: invitationOfOwner.invitor.firstName,
      };
    },
    [profile.data, sharedInvitations, clientId]
  );

  const cardIsChecked = useCallback(
    (accountId: FartherAccountId | AltAssetId | ExternalAccountId) =>
      accountsInPlan.find((pa) => pa.accountId === accountId) ? true : false,
    [accountsInPlan]
  );

  const onClickAccountCard = useCallback(
    ([accountType, account]: ManualOrExternalAccountTypeTuple) => {
      setShowDrawer(true);

      if (accountType === "ManuallyTrackedAccount") {
        setCurAccount(["ManuallyTrackedAccount", account.id]);
        setOwnerName(getResourceOwnerName([account.personId]).fullName);
        setAccountDisplayName(account.assetName);
        setFormTitle(
          `${getResourceOwnerName([account.personId]).firstName}'s ${
            account.assetName
          }`
        );

        const existingAccount = (financialPlan.data?.accounts ?? []).find(
          (a) => a.accountId === account.id
        );

        setExistingAccountData(existingAccount);
      } else {
        setCurAccount(["ExternalAccount", account.accountId]);
        setOwnerName(getResourceOwnerName([account.clientId]).fullName);
        setAccountDisplayName(
          formatExternalAccountName(getExternalAccountName(account), 26)
        );
        setFormTitle(
          `${getResourceOwnerName([account.clientId]).firstName}'s ${
            getExternalAccountName(account).name
          }`
        );

        const existingAccount = (financialPlan.data?.accounts ?? []).find(
          (a) => a.accountId === account.accountId
        );

        setExistingAccountData(existingAccount);
      }
    },
    [
      financialPlan.data,
      setShowDrawer,
      setCurAccount,
      setOwnerName,
      getResourceOwnerName,
      setAccountDisplayName,
      setExistingAccountData,
    ]
  );

  if (allAccounts.hasError || balances.hasError) {
    return <div>There was an error loading the data</div>;
  }

  const isLoadingData =
    financialPlan.isLoading || allAccounts.isLoading || balances.isLoading;

  const isMutating = isMutatingPost || isMutatingPatch || isMutatingDelete;

  const isMutatingAllCards = isMutatingPostAll || isMutatingDeleteAll;

  const liabilityGoals =
    financialPlan?.data?.goals.filter(
      (g) => g.financeMethod === GoalBasedFinancingMethodSchema.Enum.Loan
    ) ?? [];

  const getCardHeading = (goalType: GoalBasedGoalType) => {
    if (client.firstName === undefined) {
      return "";
    }

    const goalTypeLabel = goalOptions.find((t) => t.id === goalType)?.label;

    if (goalTypeLabel === undefined) {
      return "";
    }

    return goalTypeLabel;
  };

  const goalAmountAndDownPaymentDiff = (
    amount?: string,
    downPayment?: string
  ): number => {
    if (amount === undefined) {
      return 0;
    }

    const amountNum = parseFloat(amount);

    if (downPayment === undefined) {
      return amountNum;
    }

    const downPaymentNum = parseFloat(downPayment);

    const diff = amountNum - downPaymentNum;

    return diff;
  };

  return (
    <>
      <PageHero
        className={styles.pageHero}
        innerClassName={styles.pageHeroInner}
        start={
          <TitleBlock
            title="Assets & Liabilities"
            subTitle="Your accounts are shown below and you can choose which ones, if any, you'd like to ignore for this plan."
          />
        }
        end={
          <LinkButton
            text="Add Account"
            variant="outlined"
            buttonType="secondary"
            to={`/Client/${clientId}/Accounts/Add`}
            sx={{ width: "240px" }}
          />
        }
        endClassName={styles.pageHeroEnd}
      />

      <BodyContentFormatter>
        <ContentWithAside
          aside={
            <>
              <div className={styles.asideContainer}>
                <AsideTooltip
                  heading={"Include in Plan"}
                  text={
                    "The accounts that are checked will be used to project your net worth into the future."
                  }
                />
              </div>

              <div className={styles.asideContainer}>
                <AsideTooltip
                  heading={"All Accounts"}
                  text={"Include all your accounts in the plan."}
                  footerComponent={
                    <div className={styles.tooltipCheckboxMarginTop}>
                      <Checkbox
                        checked={areAllAccountsIncludedInPlan}
                        onChange={handleToggleAllAccounts}
                        label={"Include all accounts"}
                        disabled={
                          allAccounts.isLoading ||
                          allAccounts.hasError ||
                          isMutatingPost ||
                          isMutatingDelete ||
                          isMutatingAllCards
                        }
                      />
                    </div>
                  }
                />
              </div>
            </>
          }
        >
          <div className={styles.takeUpSpace4} />

          <div className={styles.heading}>
            <Text variant={"heading1"} color={"text"}>
              Assets
            </Text>

            <Text variant={"body"} color={"textSubtle"}>
              {pluralize(
                allAssets.fartherAccounts.length +
                  allAssets.manuallyTrackedAccounts.length +
                  allAssets.externalAccounts.length,
                "Asset",
                true
              )}
            </Text>
          </div>

          {isLoadingData ? (
            <CardsGrid>
              <CardLoading className={styles.card} hasCheckbox={true} />

              <CardLoading className={styles.card} hasCheckbox={true} />
            </CardsGrid>
          ) : (
            <CardsGrid>
              {allAssets.fartherAccounts.map((acct) => {
                const balanceData = fartherAccountBalances.find(
                  (bal) => bal.accountId === acct.virtualAccountId
                );

                const balance =
                  balanceData !== undefined && balanceData.balance !== null
                    ? toUSD(balanceData.balance, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : "N/A";

                const [isLinked, section] = isAssetLinkedInAnotherCategoryCard(
                  acct.virtualAccountId,
                  financialPlan.data
                );

                return (
                  <Card
                    key={acct.virtualAccountId}
                    className={styles.card}
                    title={
                      balances.isLoading ? (
                        <CardTitleLoading />
                      ) : (
                        <Text variant={"title1"}>{balance}</Text>
                      )
                    }
                    subTitle={
                      <Text variant={"bodySmall"} color={"textSubtle"}>
                        Balance
                      </Text>
                    }
                    heading={
                      <Text variant={"title2"}>
                        {acct.accountDetails.accountType}
                      </Text>
                    }
                    headingSubtitle={
                      <Text variant={"bodySmall"} color={"textSubtle"}>
                        {acct.accountDetails.custodianAccountNumber
                          ? `(...${acct.accountDetails.custodianAccountNumber.slice(
                              -4
                            )})`
                          : "N/A"}
                      </Text>
                    }
                    text={
                      <Text variant="body" color={"textSubtle"}>
                        {getResourceOwnerName(acct.accountOwner).fullName}
                      </Text>
                    }
                    checkboxLabel={"Include in Plan"}
                    isChecked={cardIsChecked(acct.virtualAccountId)}
                    checkboxIsDisabled={isLinked}
                    checkboxTooltipText={genTooltipText(isLinked, section)}
                    onCheckboxChange={(bool: boolean) =>
                      onCheckboxChange(bool, [
                        "FartherManagedAccount",
                        acct.virtualAccountId,
                      ])
                    }
                    isMutating={
                      (isMutating && mutatingCard === acct.virtualAccountId) ||
                      isMutatingAllCards
                    }
                  />
                );
              })}

              {allAssets.manuallyTrackedAccounts.map((mta) => {
                const [isLinked, section] = isAssetLinkedInAnotherCategoryCard(
                  mta.id,
                  financialPlan.data
                );

                return (
                  <FilledCard
                    key={mta.id}
                    className={styles.card}
                    title={toUSD(mta.assetValue, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                    subTitle={
                      mta.assetType === "Real Estate"
                        ? "Asset Value"
                        : "Balance"
                    }
                    heading={capitalizeFirstLetter(mta.assetName)}
                    headingSubtitle={mta.assetType}
                    text={getResourceOwnerName([mta.personId]).fullName}
                    checkboxLabel={"Include in Plan"}
                    isChecked={cardIsChecked(mta.id)}
                    checkboxIsDisabled={isLinked}
                    checkboxTooltipText={genTooltipText(isLinked, section)}
                    onCheckboxChange={(bool: boolean) =>
                      onCheckboxChange(bool, ["ManuallyTrackedAccount", mta.id])
                    }
                    isMutating={
                      (isMutating && mutatingCard === mta.id) ||
                      isMutatingAllCards
                    }
                    onClick={() =>
                      onClickAccountCard(["ManuallyTrackedAccount", mta])
                    }
                  />
                );
              })}

              {allAssets.externalAccounts.map((exAcct) => {
                const balance = getExternalAccountBalance(exAcct);
                const heading = capitalizeFirstLetter(
                  exAcct.details.account.accountType
                );

                const [isLinked, section] = isAssetLinkedInAnotherCategoryCard(
                  exAcct.accountId,
                  financialPlan.data
                );

                return (
                  <FilledCard
                    key={exAcct.accountId}
                    className={styles.card}
                    title={
                      balance !== undefined
                        ? toUSD(balance, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                        : "N/A"
                    }
                    subTitle={"Balance"}
                    heading={heading}
                    headingSubtitle={getExternalAccountName(exAcct).mask}
                    text={getResourceOwnerName([exAcct.clientId]).fullName}
                    checkboxLabel={"Include in Plan"}
                    isChecked={cardIsChecked(exAcct.accountId)}
                    checkboxIsDisabled={isLinked}
                    checkboxTooltipText={genTooltipText(isLinked, section)}
                    onCheckboxChange={(bool: boolean) =>
                      onCheckboxChange(bool, [
                        "ExternalAccount",
                        exAcct.accountId,
                      ])
                    }
                    isMutating={
                      (isMutating && mutatingCard === exAcct.accountId) ||
                      isMutatingAllCards
                    }
                    onClick={() =>
                      onClickAccountCard(["ExternalAccount", exAcct])
                    }
                  />
                );
              })}
            </CardsGrid>
          )}
          <div className={styles.heading}>
            <Text variant={"heading1"} color={"text"}>
              Liabilities
            </Text>

            <Text variant={"body"} color={"textSubtle"}>
              {pluralize(
                allLiabilities.length + liabilityGoals.length,
                "Liability",
                true
              )}
            </Text>
          </div>

          {isLoadingData ? (
            <CardsGrid>
              <CardLoading className={styles.card} hasCheckbox={true} />

              <CardLoading className={styles.card} hasCheckbox={true} />
            </CardsGrid>
          ) : (
            <CardsGrid>
              {orderBy(liabilityGoals, [(g) => g.goalType], ["asc"]).map(
                (goal) => (
                  <FilledGoalCard
                    key={goal.goalId}
                    className={styles.card}
                    heading={getCardHeading(goal.goalType)}
                    endDate={goal?.endDate}
                    financeMethod={goal.financeMethod}
                    financing={goal.financing}
                    startDate={goal?.startDate}
                    clientAndSpouse={clientSpouseData}
                    repeats={goal.repeats}
                    amount={goalAmountAndDownPaymentDiff(
                      goal.amount,
                      goal?.financing?.downPayment
                    )}
                    clientFullName={getNameOfClientOrSpouse(goal.owner)}
                    goalType={goal.goalType}
                    owner={goal.owner}
                  />
                )
              )}

              {allLiabilities.map((exAcct) => {
                const balance = getExternalAccountBalance(exAcct);
                const heading = capitalizeFirstLetter(
                  exAcct.details.account.accountType
                );

                const [isLinked, section] = isAssetLinkedInAnotherCategoryCard(
                  exAcct.accountId,
                  financialPlan.data
                );

                return (
                  <FilledCard
                    key={exAcct.accountId}
                    className={styles.card}
                    title={
                      balance !== undefined
                        ? toUSD(balance, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                        : "N/A"
                    }
                    subTitle={"Balance"}
                    heading={heading}
                    headingSubtitle={getExternalAccountName(exAcct).mask}
                    text={getResourceOwnerName([exAcct.clientId]).fullName}
                    checkboxLabel={"Include in Plan"}
                    isChecked={cardIsChecked(exAcct.accountId)}
                    checkboxIsDisabled={isLinked}
                    checkboxTooltipText={genTooltipText(isLinked, section)}
                    onCheckboxChange={(bool: boolean) =>
                      onCheckboxChange(bool, [
                        "ExternalAccount",
                        exAcct.accountId,
                      ])
                    }
                    isMutating={
                      (isMutating && mutatingCard === exAcct.accountId) ||
                      isMutatingAllCards
                    }
                    onClick={() =>
                      onClickAccountCard(["ExternalAccount", exAcct])
                    }
                  />
                );
              })}
            </CardsGrid>
          )}
        </ContentWithAside>

        <AssetsAndLiabilitiesFormDrawer
          drawerOpen={showDrawer}
          onClose={onCloseDrawer}
          isMutating={isMutating || isMutatingAllCards}
          formTitle={formTitle}
          owner={ownerName}
          accountType={curAccount?.[0]}
          accountId={curAccount?.[1]}
          accountDisplayName={accountDisplayName}
          existingAccountData={existingAccountData}
          onAddOrUpdateAccountData={onAddOrUpdateAccountData}
        />
      </BodyContentFormatter>
    </>
  );
};

export default Assets;
